import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Text } from '@atlaskit/primitives/compiled/text';
import { ColorPicker } from '@atlassian/color-picker/ui';
import { isReadable } from '@atlassian/color-picker/utils/color-conversion';
import { useAutoTransitioningColors } from '@atlassian/color-picker/utils/use-autotransitioning-colors';
import { useBuiltInColorMap } from '@atlassian/color-picker/utils/use-built-in-color-map';
import { useProductDependencies } from '@atlassian/company-hub-common/controllers/use-product-dependencies';
import { resolveAdfColorForPicker } from '@atlassian/company-hub-common/hub-brand-palette';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';

import { CARDS_EXTENSION_CONFIG_BACKGROUND_ANALYTIC_SOURCE } from '../../../../constants';
import type { CardsActions } from '../../../../controllers/use-cards-extension-parameters-and-actions/types';
import { messages } from './messages';

type BackgroundColorFieldProps = {
	backgroundColor: string;
	isCompanyHub: boolean;
	textColor: string;
	updateMainFormField: CardsActions['updateMainFormField'];
};

export const BackgroundColorField = ({
	backgroundColor,
	textColor,
	isCompanyHub,
	updateMainFormField,
}: BackgroundColorFieldProps): React.JSX.Element => {
	const { isSSRProductInDarkMode } = useProductDependencies();
	const { branding } = useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap: legacyBrandMap } = branding ?? {};
	const { backgroundColorMap } = useBuiltInColorMap(isSSRProductInDarkMode);
	const theming = useHubTheming();
	const isV2 = theming?.isAdvancedThemingEnabled ?? false;
	const palette = isV2 ? theming?.palette : undefined;
	const primaryColor = isV2 ? theming?.primaryColor : undefined;
	const v2BrandMap = isV2 ? theming?.brandBackgroundColorMap : undefined;
	const resolvedTextColor =
		isV2 && palette && primaryColor
			? resolveAdfColorForPicker(textColor, palette, 'primary.1000', primaryColor)
			: textColor;
	const resolvedBackgroundColor =
		isV2 && palette && primaryColor
			? resolveAdfColorForPicker(backgroundColor, palette, 'primary.200', primaryColor)
			: backgroundColor;
	const isReadableBackgroundColor = (color: string) => isReadable(color, resolvedTextColor);
	const isReadableTextColor = (color: string) => isReadable(color, resolvedBackgroundColor);

	// When advanced theming is on, auto-flip text color between dark/light when the
	// admin picks a contrasting bg, so the text remains readable. Mirrors the
	// auto-transition behaviour used by spotlight / callout banner.
	const toStoredColor = theming?.toStoredColor;

	useAutoTransitioningColors(
		resolvedBackgroundColor,
		isReadableTextColor,
		textColor,
		(next: string) => {
			if (!isV2) return;
			const nextStored = toStoredColor ? toStoredColor(next) : next;
			updateMainFormField({ textColor: nextStored });
		},
		branding?.defaultParameters,
	);

	const handleChange = useCallback(
		(color: string) => {
			const next = isV2 && toStoredColor ? toStoredColor(color) : color;
			updateMainFormField({ backgroundColor: next });
		},
		[updateMainFormField, isV2, toStoredColor],
	);

	return (
		<Flex justifyContent="space-between" alignItems="center">
			<Text color="color.text">
				<FormattedMessage {...messages.backgroundColorLabel} />
			</Text>
			<ColorPicker
				analyticsSource={CARDS_EXTENSION_CONFIG_BACKGROUND_ANALYTIC_SOURCE}
				builtInColorMap={
					isV2 && v2BrandMap
						? v2BrandMap
						: hideDefaultColorPalette && legacyBrandMap
							? legacyBrandMap
							: backgroundColorMap
				}
				isReadableColor={isReadableBackgroundColor}
				onChange={handleChange}
				value={resolvedBackgroundColor}
				pickerTitle={messages.backgroundColorLabel}
				showCustomPalette={!!isCompanyHub}
			/>
		</Flex>
	);
};
